import UIkit from "uikit";
import { useState, useContext, useCallback, useEffect, useMemo, useRef } from "react";
import { Flex } from "uikit-react";
import { AppContexts } from "../../providers";
import ReportsService from "../../services/reports.service";
import RecordsPerPage from "../common/RecordsPerPage";
import Pagination from "../common/Pagination";
import { useCookies } from "react-cookie";

const ALL = "__ALL__";

const PropertyFieldValues = () => {
  const ReportsContext = useContext(AppContexts.ReportsContext);
  const { macros, getMacros } = ReportsContext;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFields, setSelectedFields] = useState([ALL]);
  const [loading, setLoading] = useState(true);
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
	const [appliedSearch, setAppliedSearch] = useState("");  
  const [page, setPage] = useState(0);

  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  // total + page data from API result
  const total = useMemo(() => {
    if (!macros) return 0;
    if (Array.isArray(macros) && macros.length === 1 && typeof macros[0]?.total === "number") return macros[0].total;
    if (typeof macros?.total === "number") return macros.total;
    return 0;
  }, [macros]);

  const rentals = useMemo(() => {
    if (!macros) return [];

    if (Array.isArray(macros) && macros.length === 1 && Array.isArray(macros[0]?.paginated)) {
      return macros[0].paginated;
    }

    if (Array.isArray(macros?.paginated)) return macros.paginated;
    if (Array.isArray(macros)) return macros;

    return [];
  }, [macros]);

  const handlePageChange = useCallback((p) => setPage(p), []);

  const handleSearchChange = useCallback((v) => {
	  setSearchTerm(v);
	  setPage(0);
	}, []);

  const macroDefinitions = useMemo(() => {
	  if (!macros) return [];
	  if (Array.isArray(macros?.macro_definitions)) return macros.macro_definitions;
	  if (Array.isArray(macros?.[0]?.macro_definitions)) return macros[0].macro_definitions;
	  return [];
	}, [macros]);

	const macroOptions = useMemo(() => {
	  return [...macroDefinitions].sort((a, b) => a.macro.localeCompare(b.macro));
	}, [macroDefinitions]);

	const macroNameById = useMemo(() => {
	  const map = {};
	  macroOptions.forEach((m) => { map[String(m.id)] = m.macro; });
	  return map;
	}, [macroOptions]);

  const effectiveSelectedFields = useMemo(() => {
    if (!selectedFields?.length) return [ALL];
    if (selectedFields.includes(ALL)) return [ALL];
    return selectedFields;
  }, [selectedFields]);

	const fieldsCsv = useMemo(() => {
	  if (effectiveSelectedFields.includes(ALL)) return "";
	  return effectiveSelectedFields.map(String).join(",");
	}, [effectiveSelectedFields]);

  const visibleMacros = useMemo(() => {
	  if (!macroOptions.length) return [];

	  const chosen =
	    effectiveSelectedFields.includes(ALL)
	      ? macroOptions
	      : macroOptions.filter((m) => effectiveSelectedFields.includes(String(m.id)));

	  return chosen.map((m) => ({ id: String(m.id), label: m.macro }));
	}, [macroOptions, effectiveSelectedFields]);

  const macroLookup = useMemo(() => {
    const lookup = {};
    (rentals || []).forEach((r) => {
      const rid = String(r?.rental_id ?? "");
      const obj = r?.macros || {};
      Object.keys(obj).forEach((macroName) => {
        if (!lookup[macroName]) lookup[macroName] = {};
        lookup[macroName][rid] = obj[macroName] ?? "";
      });
    });
    return lookup;
  }, [rentals]);

  const filteredRentals = useMemo(() => {
    const list = Array.isArray(rentals) ? [...rentals] : [];

    list.sort((r1, r2) => {
      const a = (r1?.pms_id ?? "").toString();
      const b = (r2?.pms_id ?? "").toString();
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    return list;
  }, [rentals]);

  const csvHeaders = useMemo(() => {
	  return ["Property ID", ...visibleMacros.map((m) => m.label)];
	}, [visibleMacros]);

  const isAll = effectiveSelectedFields.includes(ALL);
  const dropdownRef = useRef(null);

  const selectedCountLabel = useMemo(() => {
    if (isAll) return "All Fields";
    return `${effectiveSelectedFields.length} Field${effectiveSelectedFields.length === 1 ? "" : "s"}`;
  }, [isAll, effectiveSelectedFields]);

  const toggleField = useCallback((fieldId) => {
	  setSelectedFields((prev) => {
	    if (prev.includes(ALL)) return [String(fieldId)];
	    const set = new Set(prev.map(String));
	    const k = String(fieldId);
	    if (set.has(k)) set.delete(k);
	    else set.add(k);
	    const next = Array.from(set);
	    return next.length ? next : [ALL];
	  });
	}, []);

  const selectAllFields = useCallback(() => {
    setSelectedFields([ALL]);
  }, []);

	const handleGo = useCallback(
	  (e) => {
	    e?.preventDefault?.();
	    setPage(0);
	    setAppliedSearch(searchTerm.trim());
	  },
	  [searchTerm]
	);  

  const [downloading, setDownloading] = useState(false);

  const handleDownloadCsv = useCallback(async () => {
    setDownloading(true);
    try {
      const response = await ReportsService.getMacros({
        page: 0,
        per: 100000,
        search: appliedSearch ? appliedSearch.trim() : "",
        fields: fieldsCsv,
      });
      const allData = response.data;
      const allRentals = (() => {
        if (!allData) return [];
        if (Array.isArray(allData) && allData.length === 1 && Array.isArray(allData[0]?.paginated)) {
          return allData[0].paginated;
        }
        if (Array.isArray(allData?.paginated)) return allData.paginated;
        if (Array.isArray(allData)) return allData;
        return [];
      })();

      const rows = allRentals.map((r) => {
        const propertyId = r?.pms_id ? r.pms_id : r?.rental_id;
        return [
          propertyId,
          ...visibleMacros.map((m) => r?.macros?.[String(m.id)] ?? ""),
        ];
      });

      const escapeCsvField = (val) => {
        const str = String(val ?? "");
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const headerLine = csvHeaders.map(escapeCsvField).join(",");
      const bodyLines = rows.map((row) => row.map(escapeCsvField).join(","));
      const csvContent = [headerLine, ...bodyLines].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `property_field_values_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }, [appliedSearch, fieldsCsv, visibleMacros, csvHeaders]);

  const FIRST_COL_WIDTH = 280;
  const FIELD_COL_MAX = 280;

	useEffect(() => {
    document.title = "Property Field Values - RueBaRue";
	  let mounted = true;

	  const t = setTimeout(() => {
	    (async () => {
	      try {
	        if (mounted) setLoading(true);
	        await getMacros?.({
	          page,
	          per: recordsPerPage,
	          search: appliedSearch ? appliedSearch.trim() : "",
	          fields: fieldsCsv,
	        });
	      } finally {
	        if (mounted) setLoading(false);
	      }
	    })();
	  }, 0); // no debounce needed now, but keep it if you want

	  return () => {
	    mounted = false;
	    clearTimeout(t);
	  };
	}, [page, recordsPerPage, appliedSearch, fieldsCsv]);

  return (
    <section id="property-field-values">
      <div id="hms-page-title">
        <h1 className="uk-heading-small">Property Field Values</h1>
      </div>

      <div id="hms-main-body">
        <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
          <form className="uk-form uk-margin-small-bottom" onSubmit={handleGo}>
            <div
						  className="hms-form uk-flex uk-flex-middle uk-flex-nowrap"
						>
						  <div className="uk-inline no-margin uk-margin-small-right">
						    <button
						      className="uk-button uk-button-default"
						      type="button"
						      style={{
						        display: "inline-flex",
						        alignItems: "center",
						        justifyContent: "space-between",
						        minWidth: 320,
						        gap: 12,
						        whiteSpace: "nowrap",
						      }}
						    >
						      <span style={{ lineHeight: "20px" }}>
						        Filter Fields: {selectedCountLabel}
						      </span>
						      <span uk-icon="icon: chevron-down"></span>
						    </button>

						    <div
						      ref={dropdownRef}
						      uk-dropdown="mode: click; pos: bottom-left; offset: 6; animation: uk-animation-slide-top-small; duration: 150"
						      className="uk-dropdown uk-dropdown-large uk-padding-small"
						      style={{ background: "#fff", zIndex: 9999 }}
						    >
 									<div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                    <div className="uk-text-bold">Select fields</div>
                    <div className="uk-flex uk-flex-middle uk-flex-right">
                      <button
                        className="uk-offcanvas-close uk-close-small"
                        type="button"
                        uk-close="true"
                        onClick={() => {
                          const inst = UIkit.dropdown(dropdownRef.current);
                          inst && inst.hide(false);
                        }}
                      ></button>
                    </div>
                  </div>

                  <label className="uk-flex uk-flex-middle uk-margin-small-bottom" style={{ cursor: "pointer" }}>
                    <input
                      className="hms-filter-checkbox"
                      type="checkbox"
                      checked={isAll}
                      onChange={selectAllFields}
                    />
                    <span className="uk-margin-small-left">All Fields</span>
                  </label>

                  <hr className="uk-margin-small" />

                  <div style={{ maxHeight: 300, overflow: "auto" }}>
                    {macroOptions.map((m) => {
										  const idStr = String(m.id);
										  const checked = !isAll && effectiveSelectedFields.includes(idStr);

										  return (
										    <label key={`field_${idStr}`} className="uk-flex uk-flex-middle uk-margin-small-bottom">
										      <input
										        className="hms-filter-checkbox"
										        type="checkbox"
										        checked={checked}
										        onChange={() => toggleField(idStr)}
										      />
										      <span className="uk-margin-small-left">{m.macro}</span>
										    </label>
										  );
										})}
                  </div>
						    </div>
						  </div>

						  <div className="uk-flex uk-flex-middle uk-flex-nowrap">
						    <div
						      className="uk-search uk-search-default uk-margin-small-right"
						    >
						      {searchTerm ? (
						        <a
						          className="uk-form-icon-flip uk-form-icon"
						          uk-icon="icon: close"
						          onClick={() => {
						            handleSearchChange("");
						            setAppliedSearch("");
						            setPage(0);
						          }}
						        />
						      ) : (
						        <span className="uk-search-icon-flip" uk-search-icon="true" />
						      )}

						      <input
						        className="uk-search-input uk-margin-small-right"
						        type="search"
						        placeholder="Search"
						        value={searchTerm}
						        onChange={(e) => handleSearchChange(e.target.value)}
						      />
						    </div>

						    <button
						      className="uk-button hms-btn hms-red-btn hms-go-btn uk-margin-none uk-margin-xsmall-left"
						      type="submit"
						      disabled={searchTerm.trim() === appliedSearch.trim()}
						    >
						      Go
						    </button>
						  </div>						  

							<div className="uk-flex uk-flex-middle uk-flex-nowrap uk-margin-small-left">
					      <button className="download" type="button" onClick={handleDownloadCsv} disabled={downloading}>
					        <img src="/images/download.svg" uk-tooltip="Download CSV" />
					      </button>
					    </div>

						</div>

          </form>

          <div className="uk-overflow-auto">
            {loading ? (
              <div className="uk-flex uk-flex-middle uk-flex-center" style={{ padding: 30 }}>
                <div uk-spinner="ratio: 1"></div>
                <div className="uk-margin-small-left">Loading…</div>
              </div>
            ) : (
              <form className="uk-form">
                <div className="hms-table-property-field-values">
                  <table className="uk-table uk-table-hover uk-table-divider hms-table" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: FIRST_COL_WIDTH }} />
                      {visibleMacros.map((m) => (
											  <col key={`col_${m.id}`} style={{ maxWidth: FIELD_COL_MAX, width: FIELD_COL_MAX }} />
											))}
                    </colgroup>

                    <thead>
                      <tr>
                        <th
                          style={{
                            position: "sticky",
                            left: 0,
                            top: 0,
                            zIndex: 5,
                            background: "#fff",
                            width: FIRST_COL_WIDTH,
                            minWidth: FIRST_COL_WIDTH,
                            maxWidth: FIRST_COL_WIDTH,
                          }}
                        >
                          Property ID
                        </th>
                        {visibleMacros.map((item, index) => (
                          <th
                            key={`field_${item.id}`} 
                            title={item.macro}
                            style={{
                              background: "#fff",
                              position: "relative",
                              zIndex: 1,
                              maxWidth: FIELD_COL_MAX,
                              overflow: "visible",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.label}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {filteredRentals.length > 0 ? (
                        filteredRentals.map((rental, rIndex) => (
                          <tr key={`rental_${rental?.rental_id ?? rIndex}`}>
                            <td
                              style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 4,
                                background: "#fff",
                                width: FIRST_COL_WIDTH,
                                minWidth: FIRST_COL_WIDTH,
                                maxWidth: FIRST_COL_WIDTH,
                                overflow: "visible",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                resize: "vertical",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                              title={
                                rental.pms_id
                                  ? `${rental.pms_id} ${rental.address || ""}`
                                  : `${rental.rental_id} ${rental.address || ""}`
                              }
                            >
                              <a href={"/properties/" + rental.rental_id}>
                                {rental.pms_id ? rental.pms_id : rental.rental_id}
                              </a>
                              {rental?.address ? (
                                <div
                                  className="secondary"
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: FIRST_COL_WIDTH - 20,
                                  }}
                                  title={rental.address}
                                >
                                  {rental.address}
                                </div>
                              ) : null}
                            </td>

                            {visibleMacros.map((macro, mIndex) => {
                              const value = rental?.macros?.[String(macro.id)] ?? "";

                              return (
                                <td
                                  key={`cell_${rental.rental_id}_${macro.macro}_${mIndex}`}
                                  className="message"
                                  style={{ maxWidth: FIELD_COL_MAX, overflow: "visible" }}
                                >
                                  {value && value.toString().trim() !== "" ? (
                                    <textarea
                                      disabled
                                      readOnly
                                      value={value}
                                      rows={3}
                                      style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        overflow: "auto",
                                        resize: "vertical",
                                        minHeight: 60,
                                        maxHeight: 260,
                                      }}
                                    />
                                  ) : null}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={1 + (visibleMacros?.length || 0)} style={{ padding: "30px 25px", textTransform: "none" }}>
                            No properties matching your current search/filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </form>
            )}
          </div>
        </div>
	      <Flex alignment="between top" className="guest-form-top-filters">
	        <Pagination count={total} index={page} onIndexChanged={handlePageChange} />
	        {total > 0 && <RecordsPerPage />}
	      </Flex>
      </div>

      {fieldMenuOpen ? (
        <div
          onClick={() => setFieldMenuOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 999, background: "transparent" }}
        />
      ) : null}
    </section>
  );
};

export default PropertyFieldValues;